import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LockKeyhole, Users, Rocket, TrendingUp, Percent, Wallet, Flame, RefreshCw, Loader2 } from 'lucide-react';
import { API } from '../lib/api';

// Full-screen admin dashboard. Fetches /api/admin/stats with a Bearer token
// issued by AdminLoginModal. Token lives only in memory — closing the tab
// destroys it, and a 30-min auto-lock is enforced by the parent overlay.
const fmtEth = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 });
const fmtInt = (n) => (Number(n) || 0).toLocaleString();

const AdminDashboard = ({ token, onLock }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLock();
      } else {
        setErr(e?.response?.data?.detail || e?.message || 'Failed to load admin stats');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  return (
    <div
      data-testid="admin-dashboard"
      className="fixed inset-0 z-[9998] overflow-y-auto bg-gradient-to-br from-[#050710] via-[#0a0d1a] to-[#050710] text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <span className="cf-chip"><LockKeyhole className="w-3 h-3" /> Restricted</span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">
              🔐 Admin <span className="cf-gradient-text">Dashboard</span>
            </h1>
            <p className="text-forge-muted text-sm mt-2">TheCoinForge — Platform Analytics</p>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-refresh"
              onClick={load}
              disabled={loading}
              className="cf-btn-secondary text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              data-testid="admin-lock-btn"
              onClick={onLock}
              className="cf-btn-primary text-sm"
            >
              <LockKeyhole className="w-4 h-4" /> Lock dashboard
            </button>
          </div>
        </div>

        {loading && !data && (
          <div data-testid="admin-loading" className="cf-card p-12 text-center text-forge-muted">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" />
            Reading on-chain balances across all launched tokens…
          </div>
        )}

        {err && !data && (
          <div data-testid="admin-error" className="cf-card p-6 text-[#FF6B7A] border-[#FF6B7A]/40">{err}</div>
        )}

        {data && (
          <>
            {!data.haveHistory && (
              <div className="mb-6 rounded-2xl bg-[#7DF9FF]/8 border border-[#7DF9FF]/30 px-4 py-3 text-xs text-[#7DF9FF]">
                📡 Collecting hourly revenue snapshots — daily/weekly/monthly deltas fill in over the next 30 days. For now they mirror the running total.
              </div>
            )}

            {/* Stats grid — 4 cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard testid="admin-stat-customers" icon={Users} label="Total customers"
                value={fmtInt(data.totalCustomers)} tint="cyan" />
              <StatCard testid="admin-stat-tokens" icon={Rocket} label="Tokens launched"
                value={fmtInt(data.tokensLaunched)} tint="violet" />
              <StatCard testid="admin-stat-volume" icon={TrendingUp} label="Trading volume"
                value={`${fmtEth(data.totalTradingVolume)} ETH`} tint="emerald" hint="≈ revenue × 100 (1% tax model)" />
              <StatCard testid="admin-stat-fee" icon={Percent} label="Platform fee"
                value={`${data.platformPercentage.toFixed(1)}%`} tint="amber" hint="of every transfer" />
            </div>

            {/* Revenue section — 3 cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <StatCard testid="admin-stat-received" icon={Wallet} label="💰 Amount received"
                value={`${fmtEth(data.amountReceived)} ETH`} tint="emerald" big />
              <StatCard testid="admin-stat-revenue" icon={TrendingUp} label="📈 Total revenue"
                value={`${fmtEth(data.totalRevenue)} ETH`} tint="white" big />
              <StatCard testid="admin-stat-30d" icon={Flame} label="🔥 30-day revenue"
                value={`${fmtEth(data.monthlyRevenue)} ETH`} tint="amber" big
                hint={data.haveHistory ? 'true delta from 30d ago' : 'since launch (bootstrapping)'} />
            </div>

            {/* Revenue breakdown */}
            <div className="cf-card p-5 mb-6" data-testid="admin-revenue-breakdown">
              <p className="text-xs uppercase tracking-widest text-forge-muted mb-4">Revenue breakdown</p>
              <BreakdownRow label="Daily revenue" value={`${fmtEth(data.dailyRevenue)} ETH`} testid="admin-daily" />
              <BreakdownRow label="Weekly revenue" value={`${fmtEth(data.weeklyRevenue)} ETH`} testid="admin-weekly" />
              <BreakdownRow label="Monthly revenue" value={`${fmtEth(data.monthlyRevenue)} ETH`} testid="admin-monthly" />
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                <span className="text-sm text-white font-display font-semibold">Total received</span>
                <span data-testid="admin-total-received" className="font-code text-emerald-400 font-bold">
                  {fmtEth(data.amountReceived)} ETH
                </span>
              </div>
              <p className="text-[11px] text-forge-muted mt-3 flex items-center gap-1.5">
                Paid out to platform wallet <span className="font-code text-white">{data.platformWallet?.slice(0, 6)}…{data.platformWallet?.slice(-4)}</span>
              </p>
            </div>

            {/* Customer list */}
            <div className="cf-card p-5" data-testid="admin-customer-list">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs uppercase tracking-widest text-forge-muted">Recent customers · top earners</p>
                <span className="text-xs text-forge-muted">{data.recentCustomers?.length || 0} shown</span>
              </div>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-forge-muted text-xs uppercase tracking-wider">
                      <th className="px-2 py-2 font-medium">Wallet</th>
                      <th className="px-2 py-2 font-medium">Tokens</th>
                      <th className="px-2 py-2 font-medium">Revenue generated</th>
                      <th className="px-2 py-2 font-medium">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.recentCustomers || []).map((c, i) => (
                      <tr key={c.walletFull || i} data-testid={`admin-customer-row-${i}`}
                          className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="px-2 py-3 font-code text-white">{c.wallet}</td>
                        <td className="px-2 py-3 text-[#A78BFA] font-medium">{c.tokensLaunched}</td>
                        <td className="px-2 py-3 font-code text-emerald-400">{fmtEth(c.revenueGenerated)} ETH</td>
                        <td className="px-2 py-3 text-forge-muted">{c.joinedDate}</td>
                      </tr>
                    ))}
                    {(!data.recentCustomers || data.recentCustomers.length === 0) && (
                      <tr><td colSpan={4} className="px-2 py-6 text-center text-forge-muted text-sm">No customers yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-[11px] text-forge-muted text-center mt-8">
              Session auto-locks after 30 min of inactivity · Last update {new Date(data.updatedAt).toLocaleTimeString()}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const TINTS = {
  cyan:    { bg: 'from-[#7DF9FF]/15 to-[#7DF9FF]/5 border-[#7DF9FF]/30', text: 'text-[#7DF9FF]' },
  violet:  { bg: 'from-[#A78BFA]/15 to-[#A78BFA]/5 border-[#A78BFA]/30', text: 'text-[#A78BFA]' },
  emerald: { bg: 'from-emerald-400/15 to-emerald-400/5 border-emerald-400/30', text: 'text-emerald-400' },
  amber:   { bg: 'from-[#FBBF24]/15 to-[#FBBF24]/5 border-[#FBBF24]/30', text: 'text-[#FBBF24]' },
  white:   { bg: 'from-white/10 to-white/5 border-white/15', text: 'text-white' },
};

const StatCard = ({ testid, icon: Icon, label, value, hint, tint = 'cyan', big }) => {
  const t = TINTS[tint] || TINTS.cyan;
  return (
    <div data-testid={testid} className={`rounded-3xl bg-gradient-to-br ${t.bg} border p-5 relative overflow-hidden`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${t.text}`} />
        <p className="text-xs uppercase tracking-wider text-forge-muted font-medium">{label}</p>
      </div>
      <p className={`font-display font-extrabold ${big ? 'text-3xl' : 'text-2xl'} ${t.text} mt-1 break-words`}>
        {value}
      </p>
      {hint && <p className="text-[11px] text-forge-muted mt-2">{hint}</p>}
    </div>
  );
};

const BreakdownRow = ({ label, value, testid }) => (
  <div data-testid={testid} className="flex items-center justify-between py-2 text-sm">
    <span className="text-forge-muted">{label}</span>
    <span className="font-code text-white">{value}</span>
  </div>
);

export default AdminDashboard;
